import {
  logEvent,
  setUserId,
  setUserProperties,
} from "firebase/analytics";
import firebaseConfig from "../../config/firebaseConfig";
import { shouldEnableAnalytics } from "./analyticsEnvironment";
import { applyFirebaseAnalyticsDebugGlobal } from "./applyFirebaseAnalyticsDebugGlobal";
import { acquireFirebaseAnalytics } from "./firebaseAnalyticsInstance";

/** @type {import('firebase/analytics').Analytics | null} */
let analyticsInstance = null;
let initAttempted = false;

/** @type {typeof logEvent | null} */
let logEventFn = null;
/** @type {typeof setUserId | null} */
let setUserIdFn = null;
/** @type {typeof setUserProperties | null} */
let setUserPropertiesFn = null;

/**
 * Injectable Firebase Analytics surface for unit tests.
 * @typedef {{
 *   logEvent: typeof logEvent;
 *   setUserId: typeof setUserId;
 *   setUserProperties: typeof setUserProperties;
 * } | null} AnalyticsBackendOverride
 */

/** @type {AnalyticsBackendOverride} */
let backendOverride = null;

function resolveMeasurementId() {
  return (
    process.env.REACT_APP_FIREBASE_MEASUREMENT_ID ||
    firebaseConfig.measurementId ||
    ""
  );
}

/**
 * Replace the Firebase Analytics backend (tests only).
 * @param {AnalyticsBackendOverride} backend
 */
export function setAnalyticsBackendForTests(backend) {
  backendOverride = backend;
}

export function resetAnalyticsBackendForTests() {
  backendOverride = null;
  analyticsInstance = null;
  initAttempted = false;
  logEventFn = null;
  setUserIdFn = null;
  setUserPropertiesFn = null;
}

/**
 * Lazy-init Firebase Analytics for the default Firebase app.
 * @returns {Promise<boolean>}
 */
export async function initFirebaseAnalyticsBackend() {
  if (initAttempted) {
    return Boolean(
      (analyticsInstance || backendOverride) &&
        logEventFn &&
        setUserIdFn &&
        setUserPropertiesFn
    );
  }
  initAttempted = true;

  if (!shouldEnableAnalytics()) return false;
  if (!resolveMeasurementId()) return false;

  applyFirebaseAnalyticsDebugGlobal();

  if (backendOverride) {
    logEventFn = backendOverride.logEvent;
    setUserIdFn = backendOverride.setUserId;
    setUserPropertiesFn = backendOverride.setUserProperties;
    return true;
  }

  try {
    analyticsInstance = await acquireFirebaseAnalytics();
    if (!analyticsInstance) return false;
    logEventFn = logEvent;
    setUserIdFn = setUserId;
    setUserPropertiesFn = setUserProperties;
    return true;
  } catch {
    analyticsInstance = null;
    return false;
  }
}

export function isFirebaseAnalyticsReady() {
  return Boolean(
    (analyticsInstance || backendOverride) &&
      logEventFn &&
      setUserIdFn &&
      setUserPropertiesFn
  );
}

function analyticsTarget() {
  return analyticsInstance || /** @type {import('firebase/analytics').Analytics} */ ({});
}

/**
 * @param {string} name
 * @param {import('./analytics.types').AnalyticsEventParams} params
 */
export function emitAnalyticsEvent(name, params) {
  if (!shouldEnableAnalytics() || !logEventFn) return;
  queueMicrotask(() => {
    try {
      logEventFn(analyticsTarget(), name, params);
    } catch {
      /* unavailable or blocked */
    }
  });
}

/**
 * @param {string | null} userId
 */
export function emitAnalyticsUserId(userId) {
  if (!shouldEnableAnalytics() || !setUserIdFn || !userId) return;
  queueMicrotask(() => {
    try {
      setUserIdFn(analyticsTarget(), userId);
    } catch {
      /* unavailable */
    }
  });
}

/**
 * @param {import('./analytics.types').AnalyticsUserProperties} properties
 */
export function emitAnalyticsUserProperties(properties) {
  if (!shouldEnableAnalytics() || !setUserPropertiesFn) return;
  queueMicrotask(() => {
    try {
      setUserPropertiesFn(analyticsTarget(), properties);
    } catch {
      /* unavailable */
    }
  });
}
